import { FC, useEffect, useState } from "react"
import { KSpan, KTitleSpan } from "kahuna-base-react-components"
import { userPayments } from "../../../../apis"
import { mapListToCamelCase } from "../../../../utility"
import Loading from "../../../ui/Loading"
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/solid"
import Pagination from "../../../ui/table/Pagination"

type BatchSummary = {
  id: number
  date: string
  batchId: string
  totalAmount: number
  userCount: number
  paymentCount: number
}

type BatchPayment = {
  id: number
  userEmail: string
  userFirstName: string
  userLastName: string
  date: string
  paidAmount: number
  currencyName: string
  paymentDay: string
  paymentType: string
  transactionId: string | null
}

const PaymentBatches: FC = () => {
  const limit = 10
  const [loading, setLoading] = useState(false)
  const [batches, setBatches] = useState<BatchSummary[]>([])
  const [count, setCount] = useState(0)
  const [pageNumber, setPageNumber] = useState(1)
  const [expandedBatchId, setExpandedBatchId] = useState<string | null>(null)
  const [batchPayments, setBatchPayments] = useState<BatchPayment[]>([])
  const [detailLoading, setDetailLoading] = useState(false)

  useEffect(() => {
    fetchBatches(pageNumber)
  }, [pageNumber])

  const fetchBatches = async (page: number) => {
    const offset = (page - 1) * limit
    setLoading(true)
    setExpandedBatchId(null)
    setBatchPayments([])
    try {
      const response = await userPayments(`/payment_batches/?limit=${limit}&offset=${offset}`, "get")
      if (response.status === 200) {
        const mappedBatches = mapListToCamelCase(response.data.results || []) as BatchSummary[]
        const hasPaginatedCount = typeof response.data.count === "number"

        setBatches(hasPaginatedCount ? mappedBatches : mappedBatches.slice(offset, offset + limit))
        setCount(hasPaginatedCount ? response.data.count : mappedBatches.length)
      }
    } catch {
      // ignore
    } finally {
      setLoading(false)
    }
  }

  const fetchBatchDetail = async (batchId: string) => {
    setDetailLoading(true)
    try {
      const response = await userPayments(`/payment_batch_detail/?batch_id=${batchId}`, "get")
      if (response.status === 200) {
        setBatchPayments(mapListToCamelCase(response.data.results))
      }
    } catch {
      setBatchPayments([])
    } finally {
      setDetailLoading(false)
    }
  }

  const toggleBatch = (batchId: string) => {
    if (expandedBatchId === batchId) {
      setExpandedBatchId(null)
      setBatchPayments([])
    } else {
      setExpandedBatchId(batchId)
      fetchBatchDetail(batchId)
    }
  }

  const formatPaymentDay = (day: string) => {
    if (!day || day.length !== 8) return day || "-"
    return `${day.substring(0, 4)}.${day.substring(4, 6)}.${day.substring(6, 8)}`
  }

  if (loading) return <Loading />

  return (
    <div className="w-full">
      <div className="mb-5 px-2">
        <KTitleSpan text="Payment Batches" fontSize={20} lineHeight="26px" />
      </div>

      {batches.length === 0 ? (
        <div className="text-center py-8 bg-white rounded-[14px] border border-[#11111114]">
          <KSpan text="No payment batches found." color="#666" />
        </div>
      ) : (
        <div className="bg-white rounded-[14px] border border-[#11111114] overflow-hidden">
          <div className="grid grid-cols-5 px-6 py-4 bg-[#FAFAFA]" style={{ borderBottom: "1px solid #e5e5e5" }}>
            <KSpan text="Batch ID" color="#111" fontWeight={500} fontSize={14} />
            <KSpan text="Date" color="#111" fontWeight={500} fontSize={14} />
            <KSpan text="Users" color="#111" fontWeight={500} fontSize={14} />
            <KSpan text="Payments" color="#111" fontWeight={500} fontSize={14} />
            <KSpan text="Total Amount" color="#111" fontWeight={500} fontSize={14} />
          </div>

          <div className="flex flex-col">
            {batches.map((batch) => {
              const isExpanded = expandedBatchId === batch.batchId
              return (
                <div key={batch.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                  <div
                    className="grid grid-cols-5 px-6 py-4 cursor-pointer hover:bg-gray-50 items-center"
                    onClick={() => toggleBatch(batch.batchId)}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {isExpanded ? (
                        <ChevronDownIcon className="w-4 h-4 text-gray-500 min-w-4" />
                      ) : (
                        <ChevronRightIcon className="w-4 h-4 text-gray-500 min-w-4" />
                      )}
                      <KSpan
                        text={batch.batchId.length > 28 ? `${batch.batchId.substring(0, 28)}...` : batch.batchId}
                        fontSize={13}
                      />
                    </div>
                    <KSpan text={batch.date} />
                    <KSpan text={`${batch.userCount}`} />
                    <KSpan text={`${batch.paymentCount}`} />
                    <KSpan
                      text={
                        batch.totalAmount
                          ? batch.totalAmount.toLocaleString("en-EN", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })
                          : "0"
                      }
                      fontWeight={500}
                    />
                  </div>

                  {isExpanded && (
                    <div className="bg-[#FAFAFA] px-6 py-4">
                      {detailLoading ? (
                        <div className="py-6 text-center">
                          <KSpan text="Loading..." color="#666" />
                        </div>
                      ) : batchPayments.length === 0 ? (
                        <div className="py-6 text-center">
                          <KSpan text="No payments in this batch." color="#666" />
                        </div>
                      ) : (
                        <div className="rounded-[12px] border border-[#11111112] overflow-hidden bg-white">
                          <div className="grid grid-cols-6 px-4 py-3 bg-[#F7F7F7]" style={{ borderBottom: "1px solid #e0e0e0" }}>
                            <KSpan text="User" color="#666" fontWeight={500} fontSize={12} />
                            <KSpan text="Period" color="#666" fontWeight={500} fontSize={12} />
                            <KSpan text="Payment Day" color="#666" fontWeight={500} fontSize={12} />
                            <KSpan text="Type" color="#666" fontWeight={500} fontSize={12} />
                            <KSpan text="Amount" color="#666" fontWeight={500} fontSize={12} />
                            <KSpan text="Transaction ID" color="#666" fontWeight={500} fontSize={12} />
                          </div>

                          {batchPayments.map((payment) => (
                            <div
                              key={payment.id}
                              className="grid grid-cols-6 px-4 py-3 items-start"
                              style={{ borderBottom: "1px solid #f5f5f5" }}
                            >
                              <div>
                                <KSpan
                                  text={`${payment.userFirstName || ""} ${payment.userLastName || ""}`.trim() || "-"}
                                  fontSize={13}
                                />
                                <div className="mt-1">
                                  <KSpan text={payment.userEmail || "-"} fontSize={11} color="#888" />
                                </div>
                              </div>
                              <KSpan text={payment.date || "-"} fontSize={13} />
                              <KSpan text={formatPaymentDay(payment.paymentDay)} fontSize={13} />
                              <KSpan
                                text={payment.paymentType === "regular" ? "Scheduled" : "Advance"}
                                fontSize={13}
                              />
                              <KSpan
                                text={`${payment.paidAmount.toLocaleString("en-EN", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })} ${payment.currencyName || ""}`}
                                fontWeight={500}
                                fontSize={13}
                              />
                              <KSpan
                                text={payment.transactionId ? `${payment.transactionId.substring(0, 16)}...` : "-"}
                                fontSize={11}
                                color="#888"
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          <Pagination
            hidden={count <= limit}
            pageCount={Math.ceil(count / limit)}
            currentPage={pageNumber}
            onClickPage={setPageNumber}
          />
        </div>
      )}
    </div>
  )
}

export default PaymentBatches
